import { memo, useMemo } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Key, LIVE_COLORS } from "./RadarSpectrum";
import { timeTicks } from "./RadarWaterfall";
import { More, Segment } from "./radarUi";
import { duration } from "utils/fieldFormat";
import { finite, formatMm as mm, sgtTime as sgt } from "utils/radarDiagnostics";
import {
  STATES,
  analysisStatus,
  profileFacts,
  readAnalysis,
  stateRuns,
  verdict,
} from "utils/radarAnalysis";
import { BURST_WINDOWS, burstKind, burstPicks } from "utils/radarBurst";
import {
  GAUGE_COLOR,
  RAIN_COLORS,
  RAIN_LABELS,
  gaugeMarks,
  rainMarks,
} from "utils/radarRain";
import { batchState } from "utils/radarRich";
import { soilReading, soilReference, soilReferenceText } from "utils/radarSoil";
import { windowText } from "utils/radarStream";

// Radar analysis, at the top: what the radar reads for the newest burst
// beside our estimate (dry, wet or unknown, with a confidence), why, and a
// timeline of both with the state behind them over 24 h, 7 or 30 days: one
// point a burst, so with rich readings one a wake (every 10 minutes); over
// 7 and 30 days the server thins them out, keeping of each spell of time the
// bursts with the lowest and highest of both distances, so the points shown
// are bursts still. `newest`: the newest burst as GET radar/bursts/ serves it
// (its `analysis` when the server has one); `analysis`: useRadarAnalysis's
// answer; `uploadSeconds`: how often the node uploads (rich readings come
// with its uploads, so the newest can be that old); `rain`: the rain events
// of the window (readRain()), marked along the top of the timeline, and
// `gauge` the rain gauge (readGauge()), its rain marked just below them. From
// firmware .62 a node can hold a rich wake's raw captures back and send its
// reading alone: the server decides such a wake from the node's own estimate
// and echo list (heldBack), and the timeline draws it hollow. With a soil
// surface on the node's profile the server gives each reading against it
// (utils/radarSoil): a line under the figures, and in the timeline's tip.
const clock = (ms) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));
const day = (ms) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    day: "numeric",
    month: "short",
  }).format(new Date(ms));

export function TimelineTip({ active, payload, soil = null }) {
  const row = active ? payload?.[0]?.payload : null;
  if (!row || row.gap) return null;
  const water = soilReading(
    { depthMm: row.depth, depthBelowTube: row.belowTube },
    soil,
  );
  return (
    <div className="field-burst-tip">
      <b>
        {sgt(new Date(row.ms).toISOString())} ·{" "}
        {row.held
          ? "a rich reading held back on the node"
          : row.kind === "rich"
            ? "a rich reading"
            : "a burst"}
      </b>
      <span>
        Radar's own reading{" "}
        {row.radar === null ? "nothing valid" : mm(row.radar)}
      </span>
      <span>
        Our estimate {row.ours === null ? "none" : mm(row.ours)} ·{" "}
        {STATES[row.state].label.toLowerCase()}
        {row.confidence !== null ? ` · confidence ${row.confidence} %` : ""}
      </span>
      {water && water.kind !== "unknown" && <span>{water.text}</span>}
      {row.held && (
        <span>
          Held back on the node: decided from its echo list, without the raw
          capture
        </span>
      )}
    </div>
  );
}

// Our estimate's points: a rich wake held back on the node as a ring (it
// rests on the node's echo list), the others filled while points are few
// enough for dots.
const HELD_R = 3.2;
function oursDot(dots) {
  return function OursDot({ key, cx, cy, payload }) {
    if (!Number.isFinite(cx) || !Number.isFinite(cy) || payload?.ours == null)
      return null;
    if (payload.held)
      return (
        <circle
          key={key}
          className="field-analysis-held"
          cx={cx}
          cy={cy}
          r={HELD_R}
          fill="#ffffff"
          stroke={LIVE_COLORS.ours}
          strokeWidth={1.6}
        />
      );
    return dots ? (
      <circle
        key={key}
        cx={cx}
        cy={cy}
        r={2.5}
        fill={LIVE_COLORS.ours}
        stroke="none"
      />
    ) : null;
  };
}

// Points beyond this are drawn as lines only.
const DOTS_UP_TO = 200;
// Rows for the chart, with a break wherever no burst came for much longer
// than usual, so the lines do not bridge it.
function timelineRows(points) {
  const gaps = points
    .slice(1)
    .map((p, i) => p.ms - points[i].ms)
    .filter((g) => g > 0)
    .sort((a, b) => a - b);
  const typical = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 0;
  const hold = Math.max(2 * 3600000, 3 * typical);
  const rows = [];
  points.forEach((p, i) => {
    if (i && p.ms - points[i - 1].ms > hold)
      rows.push({
        ms: (p.ms + points[i - 1].ms) / 2,
        radar: null,
        ours: null,
        gap: true,
      });
    rows.push({
      ms: p.ms,
      radar: p.radarMm,
      ours: p.ourMm,
      kind: p.kind,
      state: p.state,
      confidence: p.confidence,
      burstId: p.burstId,
      held: !!p.heldBack,
      depth: p.depthMm ?? null,
      belowTube: !!p.depthBelowTube,
    });
  });
  return rows;
}

const Timeline = memo(function Timeline({
  points,
  from,
  to,
  minutes,
  onPick,
  marks = [],
  gaugeRain = [],
  soil = null,
}) {
  const rows = useMemo(() => timelineRows(points), [points]);
  const runs = useMemo(() => stateRuns(points), [points]);
  const values = points
    .flatMap((p) => [p.radarMm, p.ourMm])
    .filter((v) => v !== null);
  const dots = points.length <= DOTS_UP_TO;
  const held = points.some((p) => p.heldBack);
  const top = Math.max(
    500,
    Math.ceil((Math.max(...values, 0) * 1.08) / 100) * 100,
  );
  return (
    <div className="field-analysis-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 10, right: 14, bottom: 0, left: 0 }}
          onClick={(state) => {
            const row = rows[Number(state?.activeTooltipIndex)];
            // A held-back wake has no captures to open.
            if (row && !row.gap && !row.held && row.burstId !== null)
              onPick?.(row.burstId);
          }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          {runs.map((run) => (
            <ReferenceArea
              key={`${run.state}-${run.from}`}
              x1={Math.max(from, run.from)}
              x2={Math.min(to, run.to)}
              fill={STATES[run.state].band}
              fillOpacity={1}
              ifOverflow="hidden"
            />
          ))}
          {/* Rain events along the top: their first to their last reading. */}
          {marks.map((m) => (
            <ReferenceArea
              key={m.key}
              className={`field-rain-mark is-${m.stage}`}
              x1={m.x1}
              x2={m.x2}
              y1={0}
              y2={top * 0.06}
              fill={RAIN_COLORS[m.stage]}
              fillOpacity={0.9}
              ifOverflow="hidden"
            />
          ))}
          {/* The rain gauge's rain just below: its rainy 5-minute sets, the
              more rain the darker. */}
          {gaugeRain.map((m) => (
            <ReferenceArea
              key={m.key}
              className="field-gauge-mark"
              x1={m.x1}
              x2={m.x2}
              y1={top * 0.07}
              y2={top * 0.12}
              fill={GAUGE_COLOR}
              fillOpacity={Math.min(1, 0.35 + m.peak / 3)}
              ifOverflow="hidden"
            />
          ))}
          <XAxis
            dataKey="ms"
            type="number"
            domain={[from, to]}
            ticks={timeTicks(from, to, minutes)}
            allowDataOverflow
            tickFormatter={(v) => (minutes > 1440 ? day(v) : clock(v))}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            reversed
            domain={[0, top]}
            tickFormatter={(v) => v.toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" mm"
            width={70}
          />
          <Tooltip content={<TimelineTip soil={soil} />} />
          <Line
            dataKey="radar"
            name="Radar's own reading"
            stroke={LIVE_COLORS.radar}
            strokeWidth={1.5}
            dot={
              dots ? { r: 2, fill: LIVE_COLORS.radar, stroke: "none" } : false
            }
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="ours"
            name="Our estimate"
            stroke={LIVE_COLORS.ours}
            strokeWidth={2.2}
            dot={
              held
                ? oursDot(dots)
                : dots
                  ? { r: 2.5, fill: LIVE_COLORS.ours, stroke: "none" }
                  : false
            }
            activeDot={{ r: 4.5 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

function Figure({ label, value, detail, tone = "" }) {
  return (
    <div
      className={`field-analysis-figure${tone ? ` ${tone}` : ""}`}
      role="group"
      aria-label={label}
    >
      <span className="field-live-label">{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

export default function RadarSummary({
  newest = null,
  analysis,
  minutes = 1440,
  onMinutes,
  onPick,
  uploadSeconds = null,
  rain = null,
  gauge = null,
  now = Date.now(),
}) {
  const points = analysis?.points || [];
  const point = points.length ? points[points.length - 1] : null;
  const burstMs = newest ? Date.parse(newest.t_first) : null;
  // A rich wake the node held back after the newest burst (firmware .62) is
  // the newest reading: it has no burst of its own, only its analysis.
  const heldNewest =
    point?.heldBack && (burstMs === null || point.ms > burstMs + 1000)
      ? point
      : null;
  const served = heldNewest ? null : readAnalysis(newest?.analysis);
  // The newest burst's own analysis, else the newest analysed point when no
  // burst is newer than it.
  const newestMs = heldNewest ? heldNewest.ms : burstMs;
  const fromPoint =
    !served && point && (newestMs === null || point.ms >= newestMs - 1000)
      ? point
      : null;
  const ours = served
    ? {
        state: served.state,
        ourMm: served.ourMm,
        confidence: served.confidence,
        radarMm: served.radarMm,
        depthMm: served.depthMm,
        depthBelowTube: served.depthBelowTube,
      }
    : fromPoint;
  // The water against the soil surface: only with one on the profile.
  const profile = analysis?.profile ?? null;
  const soil = useMemo(() => soilReference(profileFacts(profile)), [profile]);
  const water = ours ? soilReading(ours, soil) : null;
  const picks = newest && !heldNewest ? burstPicks(newest.captures) : null;
  const radarMm = heldNewest
    ? heldNewest.radarMm
    : (served?.radarMm ??
      picks?.median ??
      finite(newest?.reading?.distance_mm) ??
      fromPoint?.radarMm ??
      null);
  const at = heldNewest ? heldNewest.t : (newest?.t_first ?? point?.t ?? null);
  // The analysis's own sentence first; the comparison with the radar's
  // reading beside it.
  const said = served
    ? verdict(served)
    : fromPoint
      ? verdict({ ...fromPoint, agrees: null, echoes: [] })
      : null;
  const why = served?.why ?? null;
  const waiting = !served && !fromPoint && analysisStatus(newest) === "pending";
  const unavailable = analysis?.missing
    ? "the server does not analyse bursts yet (it needs the updated backend)"
    : waiting
      ? "the server is analysing the newest burst (a few a request); it follows within a minute"
      : newest && !served && !fromPoint
        ? "the server has not analysed the newest burst"
        : !newest && !point
          ? "no analysed burst has arrived"
          : null;
  const [chip, tone] = ours
    ? [STATES[ours.state].label, `is-${ours.state}`]
    : waiting
      ? ["Analysis pending", "is-off"]
      : ["Analysis not available yet", "is-off"];
  const pending = analysis?.pending ?? 0;
  const to = Math.max(now, point?.ms ?? -Infinity);
  const from = to - minutes * 60000;
  const inWindow = points.filter((p) => p.ms >= from);
  const states = Object.keys(STATES).filter((s) =>
    inWindow.some((p) => p.state === s),
  );
  // Rich wakes held back on the node (firmware .62); those with an estimate
  // are drawn as rings.
  const heldIn = inWindow.filter((p) => p.heldBack);
  const heldShown = heldIn.filter((p) => p.ourMm !== null);
  const marks = useMemo(() => rainMarks(rain, from, to), [rain, from, to]);
  const gaugeRain = useMemo(
    () => gaugeMarks(gauge?.series, from, to),
    [gauge, from, to],
  );
  const rained = Object.keys(RAIN_LABELS).filter((s) =>
    marks.some((m) => m.stage === s),
  );
  // A thinned-out timeline shows some of the window's analysed bursts: the
  // server says how many there are (its total).
  const thinning = analysis?.thinning ?? null;
  const thinned = !!thinning?.decimated;
  const analysed = thinned
    ? Math.max(inWindow.length, thinning.bursts)
    : inWindow.length;
  // A rich reading comes with the node's uploads: up to an upload interval
  // old is normal, not stale.
  const rich = heldNewest
    ? true
    : newest
      ? burstKind(newest) === "rich"
      : false;
  const newestAge = newestMs !== null ? now - newestMs : null;
  const batch = rich ? batchState(newestAge, uploadSeconds) : null;
  const batchWords =
    batch === "fresh"
      ? ` · up to ${duration(uploadSeconds)} old is normal (it comes with the node's uploads)`
      : batch === "overdue"
        ? ` · overdue: the node uploads every ${duration(uploadSeconds)}`
        : rich
          ? " · up to an upload interval old is normal (it comes with the node's uploads)"
          : "";

  return (
    <section
      className="field-panel field-analysis"
      aria-labelledby="radar-summary-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="radar-summary-title">Latest reading</h2>
          <p>
            {at
              ? `${sgt(at)} · ${heldNewest ? "a rich reading, held back on the node" : rich ? "a rich reading" : "a burst"}${batchWords}`
              : "Waiting for a burst"}{" "}
            · <a href="#radar-explain-title">How to read this page</a>
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {chip}
        </span>
      </div>
      <div className="field-panel-body">
        <div className="field-analysis-figures">
          <Figure
            label="Radar's own reading"
            value={radarMm !== null ? mm(radarMm) : "—"}
            detail={
              heldNewest
                ? "The distance the wake reported"
                : served?.radarMm !== null && served?.radarMm !== undefined
                  ? "Its own pick for this burst, as our analysis took it"
                  : picks?.count
                    ? `Its own pick: the median of ${picks.count} capture${picks.count === 1 ? "" : "s"}`
                    : newest?.reading
                      ? "The distance the wake reported"
                      : radarMm !== null
                        ? "Its own pick for this burst"
                        : "No valid reading"
            }
          />
          <Figure
            label="Our estimate"
            tone={ours ? `is-ours is-${ours.state}` : "is-ours"}
            value={
              ours
                ? ours.ourMm !== null
                  ? mm(ours.ourMm)
                  : "No estimate"
                : "—"
            }
            detail={
              ours ? (
                <>
                  <span className={`field-state-pill is-${ours.state}`}>
                    {STATES[ours.state].label}
                  </span>
                  {ours.confidence !== null
                    ? ` · confidence ${ours.confidence} %`
                    : ""}
                </>
              ) : (
                `${waiting ? "Analysis pending" : "Analysis not available yet"}: ${unavailable || "waiting for the server"}.`
              )
            }
          />
        </div>
        {water && (
          <div
            className={`field-analysis-soil is-${water.kind}`}
            role="group"
            aria-label="The water against the soil surface"
          >
            <strong>{water.text}</strong>
            {water.irrigate && (
              <span className="field-analysis-awd">
                Time to irrigate by the common AWD rule (water at −15 cm or lower)
              </span>
            )}
            <small>{soilReferenceText(soil)}</small>
          </div>
        )}
        {why && (
          <p
            className={`field-analysis-why is-${ours?.state ?? "unknown"}`}
            aria-label="Our analysis, in a sentence"
          >
            {why}
          </p>
        )}
        {said && (
          <p className={`field-analysis-verdict is-${said.tone}`}>
            {said.text}
          </p>
        )}
        {heldNewest && (
          <p className="field-caption" style={{ margin: 0 }}>
            Held back on the node: decided from the on-node estimate and its
            echo list, without the beat-signal checks (obstruction, lens, two
            reflectors). Raw uploads, on Node Control, sets how many are held
            back.
          </p>
        )}
        {served?.reasons.length > 0 && (
          <ul className="field-analysis-reasons" aria-label="Why">
            {served.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        <div
          className="field-analysis-timeline"
          role="group"
          aria-labelledby="radar-timeline-title"
        >
          <div className="field-stream-toolbar">
            <div>
              <h3 id="radar-timeline-title" className="field-radar-heading">
                Over the last {windowText(minutes)}
              </h3>
              <p className="field-caption" style={{ margin: 0 }}>
                {analysed.toLocaleString()} analysed burst
                {analysed === 1 ? "" : "s"}
                {thinned
                  ? ` · ${inWindow.length.toLocaleString()} shown, thinned out by the server`
                  : ""}
                {heldIn.length
                  ? ` · ${heldIn.length.toLocaleString()} of ${thinned ? "those shown" : "them"} rich wakes held back on the node`
                  : ""}
                {pending > 0 && (
                  <span className="field-analysis-pending" role="status">
                    {` · ${pending.toLocaleString()} still being analysed: the server analyses a few each time this view asks, so the timeline may take a minute to fill`}
                  </span>
                )}
              </p>
            </div>
            <Segment
              label="Window"
              options={BURST_WINDOWS}
              value={minutes}
              onChange={(m) => onMinutes?.(m)}
            />
          </div>
          {analysis?.missing ? (
            <div className="field-notice">
              The timeline needs the server's analysis of the bursts (GET
              radar/analysis/), which this server does not provide yet.
            </div>
          ) : analysis?.error ? (
            <div className="field-alert" role="alert">
              The analysis could not load: {analysis.error.message}. The page
              tries again with its refresh.
            </div>
          ) : !analysis?.loaded ? (
            <p className="field-caption">Loading the analysis…</p>
          ) : !inWindow.length ? (
            <div className="field-radar-empty">
              {pending > 0
                ? `No burst of the last ${windowText(minutes)} is analysed yet; ${pending.toLocaleString()} wait for the server, which analyses a few each time this view asks.`
                : `No analysed bursts in the last ${windowText(minutes)}. Choose a longer window, or turn bursts on in Radar rich data on Node Control.`}
            </div>
          ) : (
            <>
              <Timeline
                points={inWindow}
                from={from}
                to={to}
                minutes={minutes}
                onPick={onPick}
                marks={marks}
                gaugeRain={gaugeRain}
                soil={soil}
              />
              <ul className="field-radar-legend" aria-label="Timeline key">
                <Key color={LIVE_COLORS.radar}>Radar's own reading</Key>
                <Key color={LIVE_COLORS.ours}>Our estimate</Key>
                {heldShown.length > 0 && (
                  <Key kind="hollow" color={LIVE_COLORS.ours}>
                    Held back on the node
                  </Key>
                )}
                {states.map((s) => (
                  <Key key={s} kind="area" color={STATES[s].band}>
                    {STATES[s].label}
                  </Key>
                ))}
                {rained.map((s) => (
                  <Key key={`rain-${s}`} kind="area" color={RAIN_COLORS[s]}>
                    Rain, {RAIN_LABELS[s].toLowerCase()}
                  </Key>
                ))}
                {gaugeRain.length > 0 && (
                  <Key kind="area" color={GAUGE_COLOR}>
                    Rain at the gauge
                    {gauge?.distanceKm !== null &&
                    gauge?.distanceKm !== undefined
                      ? ` (${gauge.distanceKm.toLocaleString(undefined, { maximumFractionDigits: 1 })} km away)`
                      : ""}
                  </Key>
                )}
              </ul>
              <p className="field-caption">
                Distance from the radar, downwards, so rising water climbs.
                Click a point to open its burst below.
              </p>
              <More label="How to read this chart">
                <p className="field-caption">
                  One point per analysed burst: with rich readings, one a wake
                  (every 10 minutes on a 10-minute wake).
                  {thinned
                    ? ` Over the last ${windowText(minutes)} the server shows ${inWindow.length.toLocaleString()} of the ${analysed.toLocaleString()} bursts: of each spell of time, those with the lowest and the highest of our estimate and of the radar's own reading, so the extremes stay where they were.`
                    : ""}{" "}
                  The band behind is our state for each burst.
                  {marks.length
                    ? " The marks along the top are rain events (Rain, below), from their first to their last reading."
                    : ""}
                  {gaugeRain.length
                    ? " Those just below them are the rain gauge's rain, over each 5 minutes it had any: the more, the darker."
                    : ""}
                  {heldShown.length
                    ? ` Hollow points (${heldShown.length.toLocaleString()} here) are rich wakes the node held back: their raw capture was not uploaded, so the server decided them from the on-node estimate and its echo list; they have no burst to open.`
                    : ""}
                  {soil
                    ? " Point at a burst to see its water against the soil surface as well."
                    : ""}
                </p>
              </More>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
